import axios from 'axios';
import {cloneDeep} from 'lodash';
import {splitAgentFilter} from '@/utils/agentFilter';
import {splitPaymentFormatFilter} from '@/utils/paymentFormatFilter';
import {splitPolicySourceFilter} from '@/utils/policySourceFilter';

export const defaultPoliciesFilters = {
  searchQuery: '',
  clientCompanyIds: [],
  insuranceCompanyId: [],
  programIds: [],
  policyEndDateFrom: null,
  policyEndDateTo: null,
  policyExpired: null,
  policyEndDateIsEmpty: false,
  // Set by the "expires in N days" / "policy expired" shortcuts so their result set matches
  // the Renewals board, which skips settled renewals and group members. Listed here so a
  // filter reset clears them and countActiveFilters can diff against a known default.
  excludeRenewalStatuses: [],
  withoutGroupInsurancePolicyId: false,
  validityPeriod: null,
  activeStatuses: [],
  paymentStatus: [],
  paymentFormats: [],
  scheduleStatuses: [],
  groupingTypes: [],
  agentIds: [],
  invitationStatuses: [],
  agentCommissionPaymentStatuses: [],
  withoutAgentCommissionPaymentStatus: false,
  insuranceType: [],
  activationStatuses: [],
  // Allianz evidence screenshots: 'YES' = only sampled policies, 'NO' = only the
  // ones still to sample, null = no filter. Shown only to domains that have both
  // the scraper and investment features on.
  //
  // An id rather than a boolean on purpose: active-filters.js counts `false` as
  // "not filtering" (isEmptyValue), so a boolean NO would drop out of the filter
  // badge, the chips and Reset while still narrowing the table. Converted to the
  // API's boolean in getPolicies.
  hasScreenshots: null,
  // Set only by "Open in Policies" on a carrier read in All updates, which opens
  // this page in a new tab with ?scrapeJobId=. A run id and not its policy ids:
  // a whole-book read imports thousands, which fits in no query string. Filters
  // on scrape_job_policy server-side.
  scrapeJobIds: [],
  // With scrapeJobIds: CREATED or UPDATED, so a read's "policies added" and "updated by the daily
  // sync" open two different lists. Set only alongside it, by the same links (?scrapeJobOutcome=).
  scrapeJobOutcome: null,
  // Set only by "Deactivate them" on a finished carrier read, which opens this page in a new tab
  // with ?paymentImportRunId=. The twin of the payments page's own importRunId filter: that one
  // names the receipts an accept settled, this one names the policies behind them. A run id and
  // not their policy ids, for the same reason as above. Filters on payment_import_run_policy
  // server-side.
  paymentImportRunIds: [],
  // Set only by "Write to them" under "N overdue payments" on a finished carrier payments read,
  // which opens this page with ?overdueInPaymentRead=. A run id, resolved server-side by the
  // card's own overdue rule, so the page and the figure select the same people.
  overdueInScrapeJobIds: [],
  // "Carrier portal account": portal login ids, plus the MANUAL sentinel for
  // policies that came from no portal. Split apart in getPolicies.
  scrapeAccountIds: [],
  policyDashboardFilter: null,
  renewalsDashboardFilter: null,
  // New: filter by presence of policy number
  policyNumberFilter: null, // 'WITH_POLICY_NUMBER' | 'WITHOUT_POLICY_NUMBER' | null
};
const defaultState = {
  policies: {
    items: [],
    page: 0,
    totalCount: 0,
    loading: false,
  },
  allPoliciesTotalCount: null,
  // Set when the account-wide count request failed. "Unknown" must never be read as
  // "zero": doing so turned the first-run onboarding overlay on for accounts with
  // thousands of policies and made the whole page pointer-events: none.
  allPoliciesTotalCountFailed: false,
  // Whether the current policies list request has come back at all, and whether it failed.
  // policies.totalCount starts at 0, so without these a failed list renders "0 pólizas".
  policiesLoaded: false,
  policiesLoadError: false,
  policyDetails: {
    data: null,
    loading: false,
    insuredList: {
      items: [],
      page: 0,
      totalCount: 0,
      loading: false,
    },
    actions: {
      items: [],
      totalCount: 0,
      loading: false,
    },
    documents: []
  },
};
const state = {
  ...cloneDeep(defaultState),
  policiesFilters: {...defaultPoliciesFilters},
};

const getters = {
  policiesList: () => state.policies.items,
  policiesTotalCount: () => state.policies.totalCount,
  policiesLoading: () => state.policies.loading,
  allPoliciesTotalCount: () => state.allPoliciesTotalCount,
  allPoliciesTotalCountFailed: () => state.allPoliciesTotalCountFailed,
  policiesLoaded: () => state.policiesLoaded,
  policiesLoadError: () => state.policiesLoadError,
  policiesFilters: () => state.policiesFilters,
  policyDetails: () => state.policyDetails.data,
  policyDetailsActions: () => state.policyDetails.actions.items,
  policyDetailsDocs: () => state.policyDetails.documents,
  policyDetailsInsuredList: () => state.policyDetails.insuredList.items,
  policyDetailsInsuredTotalCount: () => state.policyDetails.insuredList.totalCount,
  policyDetailsInsuredLoading: () => state.policyDetails.insuredList.loading,
  policyDetailsLoading: () => state.policyDetails.loading,
};
/**
 * Downloads one Earnings & Savings policy as the client's own TWR workbook.
 *
 * responseType matters: the CSV export below leaves it at the default and wraps
 * resp.data in a Blob, which is fine for text and silently corrupts a binary —
 * the file downloads and then fails to open. There is no global default
 * (main.js sets only baseURL and auth), so it has to be set per call.
 *
 * Returns a promise so the caller can show a spinner and surface a failure; the
 * filename comes from the server, which knows the client's name.
 */
export const downloadInvestExcel = (policyId, fallbackName) => axios
  .post('/insurance/policy/invest/excel', { policyId }, { responseType: 'blob' })
  .then((resp) => {
    const disposition = resp.headers['content-disposition'] || '';
    const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
    const fileName = encoded
      ? decodeURIComponent(encoded[1])
      : `TWR_${fallbackName || policyId}.xlsx`;

    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([resp.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }));
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  });

export const getPolicies = (page, limit, filters) => {
  const requestFilters = {
    ...filters,
    ...splitAgentFilter(filters?.agentIds),
    ...splitPaymentFormatFilter(filters?.paymentFormats),
    ...splitPolicySourceFilter(filters?.scrapeAccountIds),
    agentCommissionPaymentStatuses: filters?.agentCommissionPaymentStatuses?.filter((val) => val !== 'MISSING'),
    withoutAgentCommissionPaymentStatus: filters.agentCommissionPaymentStatuses?.includes('MISSING'),
    // The panel stores an id so the filter chips and the badge see it; the API
    // wants the tri-state boolean. undefined, not null, when unset — null would
    // still travel as an explicit field.
    hasScreenshots: { YES: true, NO: false }[filters?.hasScreenshots],
  };

  const {
    policyDashboardFilter,
    renewalsDashboardFilter,
    ...apiFilters
  } = requestFilters;

  return axios.post('/insurance/policy/list', {
    page,
    limit,
    ...apiFilters,
    policyDashboardFilter,
    renewalsDashboardFilter,
  });
};
export const getPolicy = (policyId, withInvestPaymentSchedule = false) => axios
  .post('/insurance/policy/detail', {
    policyId,
    withInvestPaymentSchedule
  });
export const POLICIES_PAGE_LIMIT = 50;
const actions = {
  fetchPolicies({
    commit,
    state
  }, {append = false} = {}) {
    commit('setPoliciesListLoading', true);
    commit('setPoliciesLoadError', false);
    return getPolicies(state.policies.page, POLICIES_PAGE_LIMIT, state.policiesFilters)
      .then((response) => {
        if (append) {
          commit('appendPolicies', response.data);
        } else {
          commit('setPolicies', response.data);
        }
        commit('setPoliciesLoaded', true);
        commit('setPoliciesListLoading', false);
      })
      .catch((error) => {
        console.error('Error fetching policies:', error);
        // A failed list is not an empty account — flag it so the view can offer a retry
        // instead of reporting the initial totalCount of 0 as the truth.
        commit('setPoliciesLoadError', true);
        commit('setPoliciesListLoading', false);
      });
  },
  // Silent refresh of invitation statuses for the already-loaded rows: no loading
  // flag (no spinner) and rows are patched in place so scroll and selection survive.
  refreshPoliciesInvitationStatuses({commit, state}) {
    if (!state.policies.items.length) return Promise.resolve();
    return getPolicies(0, state.policies.items.length, state.policiesFilters)
      .then((response) => {
        commit('patchPoliciesInvitationDetails', response.data.items || []);
      })
      .catch((error) => {
        console.error('Error refreshing invitation statuses:', error);
      });
  },
  fetchAllPoliciesTotalCount({
    commit,
  }) {
    return getPolicies(0, 1, defaultPoliciesFilters)
      .then((response) => {
        commit('setAllPoliciesTotalCount', response.data.totalCount);
      })
      .catch((error) => {
        console.error('Error fetching all policies total count:', error);
        // Leave the count unknown rather than letting `null || 0` masquerade as an
        // empty account and trigger the first-run onboarding overlay.
        commit('setAllPoliciesTotalCountFailed', true);
      });
  },

  updatePolicies({commit}, policies) {
    axios
      .post('/insurance/policy/save-bulk', {
        itemsSave: policies,
      })
      .then((response) => {
        commit('setUpdatedPolicies', policies);
      })
      .catch((error) => {
        console.error('Error updating policies:', error);
      });
  },

  deletePolicies({
    commit,
    dispatch
  }, insurancePolicyIds) {
    axios
      .post('/insurance/policy/save-bulk', {
        insurancePolicyIds,
        insurancePolicyDelete: true,
      })
      .then((response) => {
        dispatch('fetchPolicies');
        dispatch('fetchAllPoliciesTotalCount');
      })
      .catch((error) => {
        console.error('Error deleting policies:', error);
      });
  },
  deactivatePolicies({
    commit,
    dispatch
  }, data) {
    axios
      .post('/insurance/policy/save-bulk', {
        insurancePolicyIds: data.insurancePolicyIds,
        deactivateRelativePolicies: data.deactivateRelativePolicies,
        deactivate: {
          deactivationDate: data.deactivationDate,
        },
      })
      .then((response) => {
        dispatch('fetchPolicies');
        dispatch('fetchAllPoliciesTotalCount');
      })
      .catch((error) => {
        console.error('Error deleting policies:', error);
      });
  },
  getPolicies({commit}, filters) {
    return getPolicies(0, 50, filters)
      .then((response) => response.data);
  },
  activatePolicy({
    commit,
    dispatch
  }, insurancePolicyIds) {
    return axios.post('/insurance/policy/save-bulk', {
      insurancePolicyIds,
      activate: {},
    })
      .then((resp) => {
        dispatch('fetchAllPoliciesTotalCount');
        return resp.data;
      });
  },
  fetchPolicyDetails({
    commit,
    dispatch
  }, policyId) {
    commit('setDetailsLoading', true);
    // dispatch('fetchPolicyActions', policyId);
    getPolicy(policyId)
      .then((response) => {
        commit('setPolicyDetails', response.data);
        commit('setDetailsLoading', false);
      })
      .catch((error) => {
        console.error('Error fetching policy details:', error);
        commit('setDetailsLoading', false);
      });
  },
  /**
   * Re-read the carátula (or policy PDF) already linked to a policy. Returns the
   * same PolicyPrefillUiResponse as /insurance/policy/ocr-extract; nothing is
   * written. An LLM call, so it can take up to a minute.
   */
  extractLinkedPolicyDocument(_, policyId) {
    return axios
      .post('/insurance/policy/ocr-extract-linked', { policyId })
      .then((response) => response.data);
  },
  // fetchPolicyActions({ commit }, policyId) {
  //   commit('setActionsLoading', true);
  //   axios
  //     .post('/insurance/policy/audit-log/list', {
  //       policyId,
  //       page: 0,
  //       limit: 1000,
  //     })
  //     .then((response) => {
  //       // commit('setPolicyActions', response.data);
  //       commit('setActionsLoading', false);
  //     })
  //     .catch((error) => {
  //       console.error('Error fetching policy details:', error);
  //       commit('setActionsLoading', false);
  //     });
  // },
  fetchPolicyInsured({
    commit,
    state
  }, {
    groupInsurancePolicyIds,
    append = false
  } = {}) {
    if (!append) {
      commit('setPolicyInsuredPage', 0);
    }
    commit('setPolicyInsuredLoading', true);
    const page = append ? state.policyDetails.insuredList.page : 0;
    const limit = 50;

    axios
      .post('/insurance/policy/list', {
        groupInsurancePolicyIds,
        page,
        limit,
      })
      .then((response) => {
        if (append) {
          commit('appendPolicyInsuredList', response.data);
        } else {
          commit('setPolicyInsuredList', response.data);
        }
        commit('setPolicyInsuredLoading', false);
      })
      .catch((error) => {
        console.error('Error fetching policy insured list:', error);
        commit('setPolicyInsuredLoading', false);
      });
  },
  savePolicy({
    commit,
    dispatch,
    rootState,
  }, body) {
    axios
      .post('/insurance/policy/save', {
        ...body,
      })
      .then((response) => {
        dispatch('fetchPolicies');
        dispatch('fetchAllPoliciesTotalCount');
        if (body.policyId) {
          dispatch('fetchPolicyDetails', body.policyId);
        } else if (rootState.Onboarding.isOnboardingSimulated && !rootState.Onboarding.simulatedPolicyCreated) {
          commit('SET_SIMULATED_POLICY_CREATED', true);
          dispatch('checkAndCompleteSimulation');
        }
      })
      .catch((error) => {
        console.error('Error fetching policy details:', error);
      });
  },
  // Unlike savePolicy this returns its promise, so the caller can keep a dialog spinning
  // until the last delete lands and surface a failure. Sequential rather than Promise.all:
  // every /policy/save recalculates the schedule, so concurrent deletes race each other.
  async deleteAllPolicyPayments({
    dispatch
  }, {
    policyId,
    paymentIds
  }) {
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const paymentId of paymentIds) {
        // eslint-disable-next-line no-await-in-loop
        await axios.post('/insurance/policy/save', {
          policyId,
          payment: {
            delete: {
              paymentId
            },
          },
        });
      }
    } finally {
      // Refresh once at the end — a partial failure still leaves the table stale otherwise.
      dispatch('fetchPolicyDetails', policyId);
      dispatch('fetchPolicies');
    }
  },
  // The standalone "regenerate schedule" button. Regeneration has no endpoint of its own: it is
  // the same paymentChangeType the edit modal's dialog attaches to a save, sent here without any
  // field changes. Returns its promise so the confirm dialog can spin and report a failure.
  async regeneratePolicyPayments({
    dispatch
  }, policyId) {
    try {
      await axios.post('/insurance/policy/save', {
        policyId,
        paymentChangeType: 'CALCULATE_FUTURE_PAYMENTS',
      });
    } finally {
      dispatch('fetchPolicyDetails', policyId);
      dispatch('fetchPolicies');
    }
  },
  exportPolicies({
    commit,
    dispatch
  }, data) {
    axios
      .post('/insurance/policy/export', {
        ...data,
        // Export ships raw filters from the Policies/Payments pages; the agent sentinel must be
        // split out here too or the typed agentIds would reject it.
        ...splitAgentFilter(data?.agentIds),
        ...splitPaymentFormatFilter(data?.paymentFormats),
      })
      .then((resp) => {
        // Invest exports are policy exports too; only the payments/commissions flows ship as "Payments".
        const fileNames = {
          POLICY: 'Policies',
          POLICY_WITH_INVEST: 'Policies',
          POLICY_WITH_SAVINGS: 'Savings',
        };
        const fileName = fileNames[data.type] || 'Payments';

        const link = document.createElement('a');
        link.href = URL.createObjectURL(
          new Blob([resp.data], {
            type: 'text/csv',
          })
        );
        link.download = fileName;
        link.click();

        URL.revokeObjectURL(link.href);
      })
      .catch((error) => {
        console.error('Error fetching policy details:', error);
      });
  },
  addPolicyFile({
    commit,
    dispatch
  }, body) {
    axios
      .post('/insurance/policy/save', {
        ...body,
      })
      .then((response) => {
        dispatch('fetchPolicyDetails', body.policyId);
      })
      .catch((error) => {
        console.error('Error add file:', error);
      });
  },

  deletePolicyFile({
    commit,
    dispatch
  }, body) {
    axios
      .post('/insurance/policy/save', {
        ...body,
      })
      .then((response) => {
        dispatch('fetchPolicyDetails', body.policyId);
      })
      .catch((error) => {
        console.error('Error delete file:', error);
      });
  },
  updatePoliciesFilters({
    commit,
    dispatch
  }, filters) {
    commit('setPoliciesFilters', {
      ...filters,
    });
    dispatch('fetchPolicies');
    dispatch('fetchAllPoliciesTotalCount');
  },
  clearPolicyStore({commit}) {
    commit('clearPolicyStore');
  },
};

const mutations = {
  setPolicies(state, policies) {
    state.policies.items = policies.items;
    state.policies.totalCount = policies.totalCount;
  },

  appendPolicies(state, policies) {
    state.policies.items = [...state.policies.items, ...policies.items];
  },

  incrementPoliciesPage(state) {
    state.policies.page += 1;
  },
  updatePoliciesPage(state, page) {
    state.policies.page = page;
  },

  setPoliciesListLoading(state, loading) {
    state.policies.loading = loading;
  },
  setAllPoliciesTotalCount(state, totalCount) {
    state.allPoliciesTotalCount = totalCount;
    state.allPoliciesTotalCountFailed = false;
  },
  setAllPoliciesTotalCountFailed(state, failed) {
    state.allPoliciesTotalCountFailed = failed;
  },
  setPoliciesLoaded(state, loaded) {
    state.policiesLoaded = loaded;
  },
  setPoliciesLoadError(state, hasError) {
    state.policiesLoadError = hasError;
  },

  setPolicyDetails(state, details) {
    state.policyDetails.data = details;
  },
  setPolicyActions(state, actions) {
    state.policyDetails.actions = actions;
  },

  setDetailsLoading(state, loading) {
    state.policyDetails.loading = loading;
  },
  setActionsLoading(state, loading) {
    state.policyDetails.actions.loading = loading;
  },

  setUpdatedPolicies(state, updatedPolicies) {
    state.policies.items = state.policies.items.map((policy) => {
      const updatedPolicy = updatedPolicies.find((val) => val.policyId === policy.policyId);
      if (updatedPolicy) {
        return {
          ...policy,
          ...updatedPolicy,
          stage: updatedPolicy.stageSave,
        };
      }
      return policy;
    });
  },

  // Mutates existing row objects instead of replacing state.policies.items —
  // replacing the array would clear el-table selection and reset scroll.
  patchPoliciesInvitationDetails(state, freshItems) {
    const freshById = new Map(freshItems.map((item) => [item.insurancePolicyId, item]));
    state.policies.items.forEach((policy) => {
      const fresh = freshById.get(policy.insurancePolicyId);
      if (fresh && fresh.invitationDetails?.status !== policy.invitationDetails?.status) {
        Object.assign(policy, { invitationDetails: fresh.invitationDetails });
      }
    });
  },

  setPoliciesFilters(state, policiesFilters) {
    state.policies.page = 0;
    state.policiesFilters = {...state.policiesFilters, ...policiesFilters};
  },
  setPolicyInsuredList(state, data) {
    state.policyDetails.insuredList.items = data.items;
    state.policyDetails.insuredList.totalCount = data.totalCount;
  },
  appendPolicyInsuredList(state, data) {
    state.policyDetails.insuredList.items = [...state.policyDetails.insuredList.items, ...data.items];
    state.policyDetails.insuredList.totalCount = data.totalCount;
  },
  setPolicyInsuredPage(state, page) {
    state.policyDetails.insuredList.page = page;
  },
  incrementPolicyInsuredPage(state) {
    state.policyDetails.insuredList.page += 1;
  },
  setPolicyInsuredLoading(state, loading) {
    state.policyDetails.insuredList.loading = loading;
  },

  clearPolicyStore(state) {
    const {allPoliciesTotalCount, allPoliciesTotalCountFailed} = state;
    state.policies = cloneDeep(defaultState.policies);
    state.policyDetails = cloneDeep(defaultState.policyDetails);
    state.policiesFilters = cloneDeep(defaultPoliciesFilters);
    state.allPoliciesTotalCount = allPoliciesTotalCount;
    state.allPoliciesTotalCountFailed = allPoliciesTotalCountFailed;
    state.policiesLoaded = false;
    state.policiesLoadError = false;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
